import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, Alert,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  Warning as WarningIcon,
  Shield as ShieldIcon,
  Close as CloseIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getCompaniesWithUsers,
  deleteCompany,
} from '../services/adminUserService';
import CompanyTable from '../components/CompanyTable';
import CompanyDetailPanel from '../components/CompanyDetailPanel';
import CreateUserForm from '../components/CreateUserForm';
import ModuleManagementPanel from '../components/ModuleManagementPanel';

// â”€â”€ Page header icon style â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const headerIconSx = {
  width: 44, height: 44, borderRadius: 2,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};

// â”€â”€ Confirm delete dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function ConfirmDeleteDialog({ open, company, onConfirm, onClose, loading }) {
  if (!company) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: '0.95rem', color: '#f44336', pb: 1 }}>
        <WarningIcon fontSize="small" /> Delete Company
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          Permanently delete <strong>{company.name}</strong>?<br />
          This affects <strong>{company.users.length} user(s)</strong> and all associated stores.<br />
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit" sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm} variant="contained" disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
            bgcolor: '#f44336', '&:hover': { bgcolor: '#c62828' },
            '&.Mui-disabled': { bgcolor: '#e0e0e0' },
          }}
        >
          {loading ? 'Deletingâ€¦' : 'Yes, Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// â”€â”€ Main page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const AdminUserManagementPage = () => {
  const [companies, setCompanies]                     = useState([]);
  const [loading, setLoading]                         = useState(false);
  const [loadError, setLoadError]                     = useState('');

  // View: 'list' | 'detail'
  const [view, setView]                               = useState('list');
  const [selectedCompanyName, setSelectedCompanyName] = useState(null);

  // List-level dialogs
  const [deleteTarget, setDeleteTarget]               = useState(null);
  const [deletingCompany, setDeletingCompany]         = useState(false);
  const [createUserOpen, setCreateUserOpen]           = useState(false);
  const [moduleManagementOpen, setModuleManagementOpen] = useState(false);

  // â”€â”€ Data loading â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const list = await getCompaniesWithUsers();
      setCompanies(list);
    } catch (e) {
      setLoadError(e?.response?.data?.message || e?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  // Keep selectedCompany reactive after refresh
  const selectedCompany = useMemo(
    () => companies.find(c => c.name === selectedCompanyName) ?? null,
    [companies, selectedCompanyName]
  );

  const totalUsers = useMemo(
    () => companies.reduce((sum, c) => sum + c.users.length, 0),
    [companies]
  );

  // â”€â”€ Navigation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleOpenCompany = useCallback((company) => {
    setSelectedCompanyName(company.name);
    setView('detail');
  }, []);

  const handleBack = useCallback(() => {
    setView('list');
    setSelectedCompanyName(null);
  }, []);

  const handleCompanyDeleted = useCallback(() => {
    handleBack();
    loadCompanies();
  }, [handleBack, loadCompanies]);

  // â”€â”€ Delete company from list â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) {
      toast.error('Cannot delete: company ID not found');
      setDeleteTarget(null);
      return;
    }
    setDeletingCompany(true);
    try {
      await deleteCompany(deleteTarget.id);
      toast.success(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      loadCompanies();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      setDeletingCompany(false);
    }
  };

  // â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const renderHeader = () => (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      mb: 3, gap: 2, flexWrap: 'wrap',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {view === 'detail' && (
          <Tooltip title="Back to companies">
            <IconButton
              onClick={handleBack} size="small"
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mr: 0.5 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={headerIconSx}>
          <ShieldIcon sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Box>
          {view === 'list' ? (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.2, color: '#1a1a2e' }}>
                Company Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {loading
                  ? 'Loadingâ€¦'
                  : `${companies.length} compan${companies.length !== 1 ? 'ies' : 'y'} Â· ${totalUsers} user${totalUsers !== 1 ? 's' : ''} total`}
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <Typography
                  variant="caption" color="text.secondary" onClick={handleBack}
                  sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { color: '#667eea' } }}
                >
                  Companies
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea' }}>
                  {selectedCompany?.name ?? 'â€¦'}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.2, color: '#1a1a2e' }}>
                {selectedCompany?.name ?? 'â€”'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {selectedCompany
                  ? `${selectedCompany.users.length} user${selectedCompany.users.length !== 1 ? 's' : ''} Â· ${[selectedCompany.city, selectedCompany.country].filter(Boolean).join(', ') || 'No location set'}`
                  : ''}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Tooltip title="Refresh">
          <IconButton
            onClick={loadCompanies} disabled={loading}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
          >
            {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        {view === 'list' && (
          <>
            <Button
              variant="outlined"
              startIcon={<ViewModuleIcon sx={{ fontSize: 17 }} />}
              onClick={() => setModuleManagementOpen(true)}
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 1.5,
                px: 2,
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#5568d3',
                  bgcolor: '#667eea10',
                },
              }}
            >
              Modules
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon sx={{ fontSize: 17 }} />}
              onClick={() => setCreateUserOpen(true)}
              sx={{
                fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 2.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
              }}
            >
              Add Company
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {renderHeader()}

      {loadError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 1.5 }} onClose={() => setLoadError('')}>
          {loadError}
        </Alert>
      )}

      {/* Companies DataTable */}
      {view === 'list' && (
        <CompanyTable
          companies={companies}
          loading={loading}
          onOpen={handleOpenCompany}
          onDelete={c => setDeleteTarget(c)}
        />
      )}

      {/* Company detail with 3 tabs */}
      {view === 'detail' && selectedCompany && (
        <CompanyDetailPanel
          company={selectedCompany}
          onRefresh={loadCompanies}
          onCompanyDeleted={handleCompanyDeleted}
        />
      )}

      {view === 'detail' && !selectedCompany && !loading && (
        <Alert severity="info" sx={{ borderRadius: 1.5 }}>
          Company not found.{' '}
          <Button size="small" onClick={handleBack} sx={{ fontWeight: 700, textTransform: 'none' }}>
            Back to list
          </Button>
        </Alert>
      )}

      {/* â”€â”€ Dialogs â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}

      {/* Quick edit from list */}
      {/* Delete company from list */}
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        company={deleteTarget}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
        loading={deletingCompany}
      />

      {/* Add company = create first user for a new company name */}
      <Dialog
        open={createUserOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setCreateUserOpen(false); }}
        disableEscapeKeyDown
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, maxHeight: '92vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={headerIconSx}>
                <BusinessIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>Add Company</Typography>
                <Typography variant="caption" color="text.secondary">
                  Create a new company by registering its first user
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setCreateUserOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <CreateUserForm
            onCreated={() => { setCreateUserOpen(false); loadCompanies(); }}
          />
        </DialogContent>
      </Dialog>

      {/* Module Management Dialog */}
      <ModuleManagementPanel
        open={moduleManagementOpen}
        onClose={() => setModuleManagementOpen(false)}
      />
    </Box>
  );
};

export default AdminUserManagementPage;
